/* eslint-disable no-underscore-dangle */
/* eslint-disable react/jsx-props-no-spreading */
import React, {
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type RefCallback,
} from 'react';
import {
  ObservabilityErrorBoundary,
  captureError,
} from '@leagueplatform/observability';
import { useQuery } from '@tanstack/react-query';
import { debounce, isEqual } from 'lodash-es';
import {
  createTheme,
  type GDSTheme,
  type GDSThemeLoaderOptions,
} from '@leagueplatform/genesis-core';
import { useConfigProperty } from '@leagueplatform/core';

import { useMasonryEngineConfig } from '../masonry-engine-config-context';
import type {
  AnyMasonryEngineNode,
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
  NodeQueryMeta,
} from '../types/masonry-engine-node.types';
import { logError } from '../utils/log';
import { getMasonryEngineObservabilityContext } from '../utils/masonry-engine-observability-content';
import {
  MasonryEngineNodeAncestryContext,
  MasonryEngineNodeAncestryProvider,
} from '../masonry-engine-node-ancestry-context';
import { getCurrentBreakpoint } from '../utils/get-breakpoint';
import { useMasonryEngineActionEmitter } from '../masonry-engine-action-controller';
import { toStitchesTypographyScales } from '../utils/to-stitches-typography-scales';
import {
  DEFAULT_NAMESPACE,
  HIDDEN_SUBTREE_CLASS_NAME,
  MasonryEngineLevelActionNames,
  QUERY_KEY_GET_NODE_ASYNC_SELF,
} from '../constants/constants';
import { useStateControllerStore } from '../masonry-engine-state-controller';
import {
  type MasonryNodeMessageReceiver,
  type MasonryNodeMessages,
} from '../types/masonry-engine-message.types';
import { composeInitialNodeState } from '../utils/compose-initial-node-state';
import {
  SubtreeNodeVisibilityWrapper,
  useIsAncestrallyHidden,
} from './hide-descendent-nodes.component';
import { DevtoolsBridge } from './devtools-bridge.component';
import { useReload } from './use-reload';
import { InstanceModal } from './instance-modal';

type NodeResolverProps = {
  node: MasonryEngineNode;
};

/**
 * Given a {@link MasonryEngineNode `MasonryEngineNode`}, executes the node's
 * data query and passes the results to the appropriate renderer when ready.
 */
const NodeResolver = ({ node: originalNode }: NodeResolverProps) => {
  /**
   * Grab controllers from engine context.
   */
  const {
    nodeRendererRegistry,
    stateControllerStore,
    messagingController,
    getAsyncNodeData,
  } = useMasonryEngineConfig();

  const stateController = stateControllerStore.getState();

  const invokeAction = useMasonryEngineActionEmitter();

  /* --------- BEGIN ASYNC NODE LOGIC -------- */

  /**
   * we will store whatever query params object was sent to us in the latest
   * `reload` message, if any.
   *
   * We initialize with `originalNode.asyncConfig?.initialQueryParams`, which may be undefined or any object.
   */
  const [queryParams, setQueryParams] = useState(
    originalNode.asyncConfig?.initialQueryParams,
  );

  /**
   * If the INITIAL query params of this node change **from the driver's end** (i.e.,
   * someone has RECREATED a driver object with different static params and thus
   * this tree will rerender), we consider any dynamically set param values to be
   * "invalid" and we reset them to the new initial params.
   */
  useEffect(() => {
    setQueryParams((current) => {
      if (!isEqual(current, originalNode.asyncConfig?.initialQueryParams)) {
        return originalNode.asyncConfig?.initialQueryParams;
      }

      return current;
    });
  }, [originalNode.asyncConfig?.initialQueryParams]);

  const isAsync = !!originalNode.asyncConfig;

  const upperAncestry = useContext(MasonryEngineNodeAncestryContext);

  const isRoot = upperAncestry.length === 0;

  useEffect(() => {
    if (isRoot && window.__MASONRY_DEVTOOLS__) {
      window.__MASONRY_DEVTOOLS__.instanceResources?.set(originalNode.id, {
        invokeAction,
        sendMessage: messagingController.sendMessage,
        sendMessageToInstance: messagingController.sendMessageToInstance,
        setNodeState: stateController.setNodeState,
      });
    }
    return () => {
      if (isRoot && window.__MASONRY_DEVTOOLS__) {
        window.__MASONRY_DEVTOOLS__.instanceResources?.delete(originalNode.id);
      }
    };
  }, [
    invokeAction,
    isRoot,
    messagingController.sendMessage,
    messagingController.sendMessageToInstance,
    originalNode.id,
    stateController.setNodeState,
  ]);

  /**
   * Manually add the id of this node into the ancestry array,
   * which we will use when invoking engine-level actions.
   * This is not necessary inside node renderers, because they are rendered
   * inside `AncestryContext` which this component is in charge of mounting.
   */
  const ancestry = useMemo(
    () => [originalNode.id, ...upperAncestry],
    [originalNode.id, upperAncestry],
  );

  const meta: NodeQueryMeta = {
    ancestry,
  };

  const query = useQuery(
    [
      QUERY_KEY_GET_NODE_ASYNC_SELF,
      originalNode.id,
      originalNode.asyncConfig?.getterParams ?? null,
      queryParams || {},
    ],
    () => getAsyncNodeData(originalNode.asyncConfig!.getterParams, queryParams),
    {
      meta,
      enabled: isAsync,
      placeholderData: () => {
        /**
         * If there is pre-seeded data for this node, use it
         * as initial data.
         */
        const data = stateController.instance.asyncNodeCacheSeeds.get(
          originalNode.id,
        );

        if (data) {
          stateController.instance.asyncNodeCacheSeeds.delete(originalNode.id);
        }

        return data;
      },
      /**
       * we WANT to fetch when this component mounts.
       */
      refetchOnMount: true,
      /**
       * we DO NOT WANT to fetch when the tab/window is re-focused or when
       * an offline client reconnects, because we don't want to overwrite any
       * state changes that may have occurred client-side since this component mounted.
       */
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      keepPreviousData: true,
    },
  );

  /**
   * Refify queryParams to make the useReload returns more stable.
   */
  const queryParamsRef = useRef(queryParams);
  queryParamsRef.current = queryParams;

  const { reloadStatus, triggerReload } = useReload({
    id: originalNode.id,
    query,
    queryParamsRef,
    setQueryParams,
  });

  const node: AnyMasonryEngineNode =
    !isAsync || !query.data || reloadStatus === 'loader'
      ? originalNode
      : query.data;

  useEffect(() => {
    node.prefetchNodes?.forEach(async (nodeToPrefetch) => {
      if (stateController.instance.asyncNodeCacheSeeds.get(nodeToPrefetch.id)) {
        return;
      }

      const prefetchAsyncConfig = nodeToPrefetch.asyncConfig;
      if (!prefetchAsyncConfig) {
        return;
      }

      const data = await Promise.resolve(
        getAsyncNodeData(
          prefetchAsyncConfig.getterParams,
          prefetchAsyncConfig.initialQueryParams,
        ),
      );

      if (data) {
        stateController.instance.asyncNodeCacheSeeds.set(data.id, data);
      }
    });
  }, [
    getAsyncNodeData,
    node.prefetchNodes,
    stateController.instance.asyncNodeCacheSeeds,
  ]);

  /* ---------- END ASYNC NODE LOGIC --------- */

  const rootElementRef = useRef<HTMLElement>();

  const rootElementRefCallback: RefCallback<HTMLElement> = (instance) => {
    if (instance) {
      rootElementRef.current = instance;
    }
  };

  /**
   * Add a resize event listener to the window to handle the onResize action.
   * This handler will be called when the window is resized.
   */
  useEffect(() => {
    const onResizeAction = node.actions?.onResize;

    if (!onResizeAction) return undefined;

    const breakpoints = onResizeAction.payload.screenSizeBreakpoints;

    if (!Array.isArray(breakpoints)) return undefined;

    let lastBreakpoint = getCurrentBreakpoint();

    const handleResize = debounce(() => {
      const currentBreakpoint = getCurrentBreakpoint();
      if (currentBreakpoint !== lastBreakpoint) {
        lastBreakpoint = currentBreakpoint;
        if (breakpoints.includes(currentBreakpoint)) {
          invokeAction({ ...onResizeAction, ancestry });
        }
      }
    }, 250);

    window.addEventListener('resize', handleResize);

    return () => {
      handleResize.cancel();
      window.removeEventListener('resize', handleResize);
    };
  }, [ancestry, invokeAction, node.actions?.onResize]);

  /* --------- START STATE LOGIC --------- */

  /**
   * Node state derived purely from backend data, without/before any front-end mutations.
   */
  const backendDerivedNodeState = useMemo(
    () => composeInitialNodeState(node),
    [node],
  );

  /**
   * Track last time we initialized this node's state in the state controller.
   */
  const lastInitializedStateAt = useRef(0);

  useEffect(() => {
    if (!isAsync && !lastInitializedStateAt.current) {
      /**
       * This is a sync node that has not yet had its state initialized.
       * Initialize it now.
       */
      stateControllerStore
        .getState()
        .setInitialNodeState(node.id, backendDerivedNodeState);

      /**
       * TODO: Sync descendants that stay mounted across parent async reloads currently
       * retain their state. Revisit how to adopt fresh server truth here without
       * clobbering intentional client-side state.
       */
      lastInitializedStateAt.current = Date.now();

      return;
    }

    if (query.data && query.dataUpdatedAt > lastInitializedStateAt.current) {
      /**
       * This is an async node for which we just got fresh backend data since we last initialized
       * its state. Re-initialize it now.
       */

      /**
       * TODO - consider deep-diffing this fresh data to the last "fresh data"
       * and only re-init if different. this will avoid clobbering client-side
       * state changes if the backend data hasn't actually changed.
       */
      stateControllerStore
        .getState()
        .setInitialNodeState(node.id, composeInitialNodeState(query.data));

      lastInitializedStateAt.current = query.dataUpdatedAt;
    }
  }, [
    isAsync,
    node.id,
    backendDerivedNodeState,
    query.data,
    query.dataUpdatedAt,
    stateControllerStore,
  ]);

  useEffect(
    () => () => {
      /**
       * When this component unmounts, clear out this node's state from the
       * state controller.
       */
      stateControllerStore.getState().clearNodeState(node.id);
    },
    [node.id, stateControllerStore],
  );

  /**
   * Subscribe to this node's state in the state controller. Since node state
   * initialization happens in a side effect, it may not yet be available in the store
   * (i.e., if this is the first render). In that case, we just use the same object
   * that we would use to initialize the state.
   */
  const nodeState =
    useStateControllerStore((store) => store.getNodeState(node.id)) ||
    backendDerivedNodeState;

  const originalNodeState = useMemo(
    () => composeInitialNodeState(originalNode),
    [originalNode],
  );

  /**
   * Since at any given moment we may be in the middle of a reload, we need to
   * determine what the "effective" state of this node is. If we are reloading in "loader" mode,
   * we want to use the original node's properties (i.e., ignore any state changes
   * that may have happened client-side) - we don't care about any
   * "supplementary state properties". Otherwise, we use the current node state
   * from the state controller.
   */
  const effectiveState =
    reloadStatus === 'loader' ? originalNodeState : nodeState;

  /* ---------- END STATE LOGIC ---------- */

  const {
    rem: { baseSize },
  } = useConfigProperty('core.ui.theme') as GDSTheme;
  const themeOptions = useConfigProperty('core.ui.themeOptions') as
    | GDSThemeLoaderOptions
    | undefined;

  const useRems = !!themeOptions?.useRems;

  let engineClassName = useMemo(() => {
    if (
      !node.themeConfiguration ||
      !('gdsThemeTokenOverrides' in node.themeConfiguration)
    ) {
      return '';
    }

    const themeOverrides = node.themeConfiguration.gdsThemeTokenOverrides as {
      typography?: Parameters<typeof toStitchesTypographyScales>[0];
    } & Record<string, unknown>;
    const { typography, ...baseOverrides } = themeOverrides;
    const stitchesTypographyScales = toStitchesTypographyScales(
      typography ?? {},
      {
        baseSize,
        useRems,
      },
    );

    const mergedOverrides = { ...baseOverrides, ...stitchesTypographyScales };

    return createTheme(mergedOverrides);
  }, [baseSize, node.themeConfiguration, useRems]);

  const isSelfHidden = nodeState.isHidden;

  if (isSelfHidden) {
    engineClassName += ` ${HIDDEN_SUBTREE_CLASS_NAME}`;
  }

  const isHidden = useIsAncestrallyHidden() || isSelfHidden;

  /* --------- BEGIN ENGINE-LEVEL ACTION LOGIC -------- */

  /**
   * Keep track of whether we have invoked engine-level actions yet.
   */
  const hasInvokedEngineActionsRef = useRef(false);

  /**
   * If this node has its `isHidden` property set to `true`, or if any of its
   * ancestors do, then we consider it hidden.
   */

  useEffect(() => {
    if (
      !hasInvokedEngineActionsRef.current &&
      node.actions?.onLoad &&
      !isHidden
    ) {
      invokeAction({
        ...node.actions.onLoad,
        ancestry,
        origin: 'onLoad',
      });
      hasInvokedEngineActionsRef.current = true;
    }
  }, [ancestry, invokeAction, node.actions?.onLoad, node.id, isHidden]);

  /* ---------- END ENGINE-LEVEL ACTION LOGIC --------- */

  /* ---------- START MESSAGE LOGIC ---------- */

  /**
   * Refify node to make the next effect more stable.
   */
  const nodeRef = useRef(node);
  nodeRef.current = node;

  /**
   * When we mount, we create a message receiver and register it with the
   * messagingController. This receiver will receive messages sent to this
   * node/widget by whoever.
   *
   * To clean up, we remove the receiver from the map.
   */
  useEffect(() => {
    const messageReceiver: MasonryNodeMessageReceiver<MasonryNodeMessages> = {
      getQueryParams: () => queryParamsRef.current || {},
      reload: (message) => {
        if (!isAsync) {
          throw new Error('Trying to reload a sync node!');
        }
        return triggerReload(message.payload);
      },
      getRootElement: () => rootElementRef.current,
      getAncestry: () => ancestry,
      getData: () => nodeRef.current,
      serverAction: (message) =>
        invokeAction({ ...message.payload.action, ancestry }),
      focus: (message) => {
        if (rootElementRef.current) {
          const { scroll } = message.payload;
          const originalTabIndex = rootElementRef.current.tabIndex;
          rootElementRef.current.tabIndex = -1;
          rootElementRef.current.focus({ preventScroll: true });
          if (scroll) {
            const prefersReducedMotion = window.matchMedia(
              '(prefers-reduced-motion: reduce)',
            ).matches;
            rootElementRef.current.scrollIntoView({
              behavior: prefersReducedMotion ? 'auto' : scroll,
            });
          }
          rootElementRef.current.tabIndex = originalTabIndex;
        }
      },
    };

    messagingController.registerReceiver(nodeRef.current.id, messageReceiver);

    return () => {
      messagingController.unregisterReceiver(nodeRef.current.id);
    };
  }, [ancestry, invokeAction, isAsync, messagingController, triggerReload]);

  /* ---------- END MESSAGE LOGIC ---------- */

  if (query.error) {
    logError(
      `Error while making api call for node id ${originalNode.id} ${query.error}`,
    );
    captureError(
      new Error(
        `MasonryEngine - Error while making api call for node id ${originalNode.id} ${query.error}`,
      ),
      getMasonryEngineObservabilityContext({}),
    );
    return null;
  }

  /**
   * If we are cold-loading (fresh async data not yet available), render the original node
   * so its loader representation is shown. Otherwise render the fetched node.
   */

  /**
   * Assigning 'default' namespace if no namespace is provided for a node
   */
  const nodeNamespace = node.namespace || DEFAULT_NAMESPACE;
  const nodeType = node.type;
  const nodeRendererMapForNamespace =
    nodeRendererRegistry.nodeRenderers[nodeNamespace];
  const nodeRendererMapDefault =
    nodeRendererRegistry.nodeRenderers[DEFAULT_NAMESPACE];

  /**
   * To better support testing, we apply an explicit locatorId to each node, or otherwise infer what it should be
   * based on the node type and id.
   */
  const locatorId = node?.locatorId || `${nodeType}$${node.id}`;

  /** Attempt to find the node.type in the renderer map for the namespace specified. Use the default namespace if no namespace is provided is specified */
  const TargetRenderer = nodeRendererMapForNamespace?.[nodeType];
  /** In cases where a namespace _is_ specified but there is no node.type in the corresponding renderer map, attempt to find it in the default renderer map */
  const FallbackNodeRenderer = nodeRendererMapDefault[nodeType];

  const NodeRenderer = TargetRenderer || FallbackNodeRenderer;

  /** Log error when no renderer found for the namespace specified */
  if (!TargetRenderer) {
    logError(
      `Could not find renderer for namespace - ${nodeNamespace} & node type - ${node.type} !`,
    );
    captureError(
      new Error(
        `Renderer [${nodeType}] not found for namespace [${nodeNamespace}`,
      ),
      getMasonryEngineObservabilityContext({}),
    );
  }

  // No NodeRenderer means there is no TargetRenderer for the specified namespace nor a
  // FallbackNodeRenderer for the default namespace, so we log an error and return null.
  if (!NodeRenderer) {
    logError(
      `Could not find renderer for when falling back to namespace - ${DEFAULT_NAMESPACE} & node type - ${node.type} !`,
    );
    captureError(
      new Error(
        `Renderer [${nodeType}] not found when falling back to namespace [${DEFAULT_NAMESPACE}]`,
      ),
      getMasonryEngineObservabilityContext({}),
    );
    return null;
  }

  /**
   * Converting each section item of type {@link MasonryEngineNode} into a section of NodeResolver component.
   */
  const resolvedSections = node.sections
    ? Object.fromEntries(
        Object.entries(node.sections).map(([section, subnodes]) => [
          section,
          subnodes.map((subnode) => (
            <NodeResolver key={subnode.id} node={subnode} />
          )),
        ]),
      )
    : {};

  /* Filter out Masonry Engine specific actions and pass rest of
     the actions to the node renderer. */
  const filteredNodeActions = node.actions
    ? Object.fromEntries(
        Object.entries(node.actions).filter(
          ([key]) => !MasonryEngineLevelActionNames.includes(key),
        ),
      )
    : {};

  // Needed to merge properties from original node and properties from api
  // response of node in order to share properties passed by the parent node
  const nodeRendererProps: MasonryEngineNodeRendererProps<MasonryEngineNode> = {
    ...effectiveState,
    ...resolvedSections,
    ...filteredNodeActions,
    rootElementRef: rootElementRefCallback,
    nodeId: node.id,
    nodeData: node,
    engineClassName,
    locatorId,
    isReloading: !!reloadStatus,
  };

  let asyncStatus: 'initial' | 'reloading' | 'idle' | undefined = isAsync
    ? 'idle'
    : undefined;

  if (query.isFetching && !query.isFetchedAfterMount) {
    // This is an async node that has just mounted.
    asyncStatus = 'initial';
  } else if (reloadStatus) {
    // This is an async node that is currently reloading.
    asyncStatus = 'reloading';
  }

  return (
    <MasonryEngineNodeAncestryProvider nodeId={node.id}>
      <ObservabilityErrorBoundary
        errorContext={getMasonryEngineObservabilityContext({
          tags: {
            errorName: `MasonryEngine - Error while rendering Masonry Engine Node id: ${node.id}`,
          },
        })}
        onError={(err: Error) => {
          logError(
            `Error while rendering Masonry Engine Node id: ${node.id} ${err.message}`,
          );
        }}
      >
        <DevtoolsBridge
          nodeData={node}
          // TODO: remove once most people have newer extension.
          //  nodeData is sufficient.
          effectiveNode={node}
          ancestry={ancestry}
          rootElementRef={rootElementRef}
          effectiveState={effectiveState}
          asyncStatus={asyncStatus}
          queryParams={queryParams || {}}
        >
          <SubtreeNodeVisibilityWrapper isHidden={!!nodeState.isHidden}>
            <NodeRenderer {...nodeRendererProps} />
          </SubtreeNodeVisibilityWrapper>
          {isRoot && (
            <InstanceModal
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              renderNode={renderModalNode}
            />
          )}
        </DevtoolsBridge>
      </ObservabilityErrorBoundary>
    </MasonryEngineNodeAncestryProvider>
  );
};

const MemoizedNodeResolver = React.memo(NodeResolver) as typeof NodeResolver;

export { MemoizedNodeResolver as NodeResolver };

/**
 * We use this in the `InstanceModal`'s `renderNode` prop so the latter can use it
 * to render nodes inside the modal.
 *
 * This is only to spare us from an import cycle between `instance-modal.tsx` and
 * `node-resolver.component.tsx`.
 */
function renderModalNode(node: MasonryEngineNode) {
  return <NodeResolver node={node} />;
}
